// CertificateUsersPage.tsx
import { useNavigate, useParams } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { IconButton } from '@mui/material';
import { ArrowBigLeft, Download, ExternalLink, ScanEye } from 'lucide-react';
import { useGetCandidatesByCertificateIdQuery } from '@/store/api/certificateApi';
import { useEffect, useState } from 'react';
import { SearchBar } from '@/components/common/SearchBar';

export default function CertificateUsersPage() {
  const { id } = useParams();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(7);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { data, isLoading } = useGetCandidatesByCertificateIdQuery({
    id: id || '',
    search: debouncedSearch,
    page: page + 1,
    limit: pageSize,
  });
  const navigate = useNavigate();
  const getFileUrl = (c: any) => {
    if (c?.localImagePath) {
      return `https://staging-api-certificate.mstblockchain.com/${c.localImagePath}`;
    }
    if (c?.metadataUrl?.startsWith('ipfs://')) {
      return c.metadataUrl.replace('ipfs://', 'https://ipfs.io/ipfs/');
    }

    return '';
  };
  const rows =
    data?.data?.map((c: any, index: number) => ({
      id: c._id,
      serial: page * pageSize + index + 1,
      name: c.name,
      email: c.email || '-',
      wallet: c.walletAddress,
      type: c.type || '-',
      url: getFileUrl(c),
      transactionHash: c.transactionHash || '',
    })) || [];

  const handleDownload = (candidateId: string) => {
    if (!candidateId) return;

    const url = `https://staging-api-certificate.mstblockchain.com/certificates/${candidateId}/download`;

    window.open(url, '_blank');
  };
  const columns = [
    { field: 'serial', headerName: 'S.No', width: 80 },

    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'wallet', headerName: 'Wallet Address', flex: 1 },
    { field: 'type', headerName: 'Type', width: 140 },

    {
      field: 'preview',
      headerName: 'Preview',
      width: 100,
      renderCell: (params: any) => (
        <IconButton
          onClick={() => {
            const fileUrl = params.row.url;

            if (!fileUrl) return;

            if (fileUrl.match(/\.(jpg|jpeg|png|webp)$/i)) {
              window.open(fileUrl, '_blank');
              return;
            }

            window.open(
              `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`,
              '_blank'
            );
          }}
        >
          <ScanEye size={18} color="white" />
        </IconButton>
      ),
    },

    {
      field: 'download',
      headerName: 'Download',
      width: 100,

      renderCell: (params: any) => (
        <IconButton onClick={() => handleDownload(params.row.id)}>
          <Download size={18} color="white" />
        </IconButton>
      ),
    },
    {
      field: 'transaction',
      headerName: 'Transaction',
      width: 120,

      renderCell: (params: any) => (
        <IconButton
          onClick={() => {
            if (!params.row.transactionHash) return;

            window.open(params.row.transactionHash, '_blank');
          }}
        >
          <ExternalLink size={18} color="white" />
        </IconButton>
      ),
    },
  ];
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearInterval(timer);
  }, [search]);
  return (
    <div className="bg-black text-white p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <ArrowBigLeft onClick={() => navigate(-1)} />
          <h2 className="text-xl font-semibold ">Certificate Users</h2>
        </div>
        <div className=" lg:w-[386px]">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search by  Name and Wallet Address"
            className="w-64"
          />
        </div>{' '}
      </div>

      <div style={{ height: 500 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          paginationMode="server"
          rowCount={data?.pagination?.total || 0}
          paginationModel={{
            page,
            pageSize,
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          sx={{
            backgroundColor: '#000 !important',
            color: '#fff',

            '& .MuiDataGrid-main': {
              backgroundColor: '#000 !important',
            },
            '& .MuiDataGrid-overlay': {
              background: 'linear-gradient(178.14deg, #000000 1.65%, #6F72FF 180.97%)',
              color: '#fff',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#000 !important',
              color: '#fff !important',
              borderBottom: '1px solid #222',
            },

            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#000 !important',
            },

            '& .MuiDataGrid-columnHeaderTitle': {
              color: '#fff !important',
              fontWeight: 600,
            },

            '& .MuiDataGrid-row': {
              backgroundColor: '#000 !important',
              borderBottom: '1px solid #111',
            },

            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#111 !important',
            },

            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #111',
              color: '#fff',
            },

            '& .MuiCheckbox-root': {
              color: '#fff !important',
            },

            '& .MuiTablePagination-root': {
              color: '#fff',
            },

            '& .MuiDataGrid-footerContainer': {
              backgroundColor: '#000 !important',
              borderTop: '1px solid #222',
            },

            border: '1px solid #222',
          }}
        />
      </div>
    </div>
  );
}
